import { forwardRef, useEffect, useId, useRef, useState } from "react";
import type { CatalogItem, CatalogItemStatus, CatalogItemStatusInfo } from "@apex/shared";
import { cn, formatPrice } from "@apex/shared";

export interface CatalogItemRowProps {
  item: CatalogItem;
  status: CatalogItemStatus;
  reason?: string;
  relatedItems?: CatalogItemStatusInfo["relatedItems"];
  /**
   * Items that are currently selected and have a `requires` rule whose target
   * is this item. When non-empty AND `status === "selected"`, this row is
   * "auto-selected" — it's selected because something else required it.
   */
  autoSelectedBy?: { id: string; code: string; name: string }[];
  onToggle: (id: string) => void;
  onRelatedClick?: (id: string) => void;
  className?: string;
}

/**
 * Info icon button. 32px square so it clears the WCAG 2.5.8 24px target
 * minimum, and layered above the row's select overlay (`z-10`) so it is the
 * click target, not the checkbox label beneath it.
 */
function InfoButton({
  onClick,
  ariaControls,
  expanded,
}: {
  onClick: (e: React.MouseEvent) => void;
  ariaControls: string;
  expanded: boolean;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      aria-label="More info"
      aria-expanded={expanded}
      aria-controls={ariaControls}
      className={cn(
        "apex-relative apex-z-10 apex-shrink-0 apex-inline-flex apex-h-8 apex-w-8 apex-items-center apex-justify-center apex-rounded",
        "apex-text-muted-foreground hover:apex-text-foreground hover:apex-bg-muted",
        "focus-visible:apex-outline-none focus-visible:apex-ring-2 focus-visible:apex-ring-ring",
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
      </svg>
    </button>
  );
}

export const CatalogItemRow = forwardRef<HTMLDivElement, CatalogItemRowProps>(
  function CatalogItemRow(
    {
      item,
      status,
      reason,
      relatedItems,
      autoSelectedBy,
      onToggle,
      onRelatedClick,
      className,
    },
    ref
  ) {
    const isSelected = status === "selected";
    const isUnavailable = status === "unavailable";
    const isRequired = status === "required";
    const isAutoSelected =
      isSelected && (autoSelectedBy?.length ?? 0) > 0;
    const hasPrice = item.priceNet != null && item.priceNet > 0;
    const hasDescription = (item.description ?? "").trim().length > 0;

    const canToggle = !isUnavailable || isSelected;

    const popoverId = useId();
    const [infoOpen, setInfoOpen] = useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);

    // Close info popover on outside click and Escape.
    useEffect(() => {
      if (!infoOpen) return;

      const onDown = (e: MouseEvent) => {
        if (
          popoverRef.current &&
          !popoverRef.current.contains(e.target as Node)
        ) {
          setInfoOpen(false);
        }
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") setInfoOpen(false);
      };

      document.addEventListener("mousedown", onDown);
      document.addEventListener("keydown", onKey);
      return () => {
        document.removeEventListener("mousedown", onDown);
        document.removeEventListener("keydown", onKey);
      };
    }, [infoOpen]);

    const handleClick = () => {
      if (canToggle) {
        onToggle(item.id);
      }
    };

    const checkboxId = `${popoverId}-select`;

    // The native checkbox is the ONE control for selecting the row: it is the
    // tab stop (Space toggles, `disabled` is announced natively) and the
    // stretched label beneath the row makes the whole card its click target.
    // Other controls (info, related-item links) sit above that overlay with
    // `z-10`. The earlier role="button" wrapper nested every one of those
    // inside an interactive element (axe `nested-interactive`).
    return (
      <div
        ref={ref}
        className={cn(
          "apex-relative apex-flex apex-items-start apex-gap-3 apex-rounded-md apex-border apex-p-3 apex-transition-all apex-duration-200",
          "has-[:focus-visible]:apex-ring-2 has-[:focus-visible]:apex-ring-ring has-[:focus-visible]:apex-ring-offset-1",
          isSelected &&
            !isAutoSelected &&
            "apex-border-primary apex-bg-primary-light apex-border-l-[3px]",
          isAutoSelected &&
            "apex-border-primary apex-bg-primary-light apex-border-l-[3px]",
          isRequired &&
            "apex-border-warning apex-bg-warning/5 apex-border-l-[3px]",
          isUnavailable && "apex-opacity-50 apex-cursor-not-allowed",
          !isSelected &&
            !isUnavailable &&
            !isRequired &&
            "apex-border-border hover:apex-border-primary/30 hover:apex-bg-muted/50 apex-cursor-pointer",
          isSelected && !isUnavailable && "apex-cursor-pointer",
          className,
        )}
      >
        <input
          id={checkboxId}
          type="checkbox"
          checked={isSelected}
          disabled={!canToggle}
          onChange={handleClick}
          aria-label={`Select ${item.name || item.code}`}
          className={cn(
            "apex-relative apex-z-10 apex-h-4 apex-w-4 apex-shrink-0 apex-mt-0.5 apex-rounded apex-border apex-border-input apex-accent-primary apex-transition-transform apex-duration-200",
            "focus-visible:apex-outline-none",
            !canToggle && "apex-cursor-not-allowed apex-opacity-50",
            canToggle && "apex-cursor-pointer",
            isSelected && "apex-scale-110",
          )}
        />
        {/* Stretched label: makes the whole row toggle the checkbox. */}
        <label
          htmlFor={checkboxId}
          className={cn(
            "apex-absolute apex-inset-0 apex-rounded-md",
            canToggle ? "apex-cursor-pointer" : "apex-cursor-not-allowed",
          )}
        >
          <span className="apex-sr-only">Select {item.name || item.code}</span>
        </label>

        <div className="apex-flex-1 apex-min-w-0">
          {/* Name + Price */}
          <div className="apex-flex apex-items-start apex-justify-between apex-gap-2">
            <div className="apex-flex apex-items-center apex-gap-1.5 apex-min-w-0">
              {isUnavailable && (
                // Lock icon — visible reason this row is excluded by a rule.
                <span
                  aria-label="Excluded by rule"
                  title={reason}
                  className="apex-shrink-0 apex-text-muted-foreground"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </span>
              )}
              {/* h3: the list's own heading is the h2 "Select Items" and the
                  category header is a button, so h4 skipped a level. */}
              <h3 className="apex-text-left apex-font-medium apex-text-sm apex-leading-tight apex-text-foreground apex-truncate">
                {item.name || item.code}
              </h3>
            </div>

            <div className="apex-flex apex-items-center apex-gap-1 apex-shrink-0">
              {hasPrice && (
                <span className="apex-text-sm apex-font-semibold apex-text-foreground">
                  {formatPrice(item.priceNet, item.currency)}
                </span>
              )}
              {hasDescription && (
                <InfoButton
                  ariaControls={popoverId}
                  expanded={infoOpen}
                  onClick={() => setInfoOpen((v) => !v)}
                />
              )}
            </div>
          </div>

          {/* Auto-selected badge — surfaces which item drove this selection. */}
          {isAutoSelected && autoSelectedBy && (
            <p
              className="apex-mt-1 apex-text-xs apex-text-primary apex-font-medium apex-flex apex-items-center apex-gap-1"
              aria-label="Auto-selected by another item"
            >
              <span>
                Auto-selected with{" "}
                {onRelatedClick ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRelatedClick(autoSelectedBy[0].id);
                    }}
                    className="apex-relative apex-z-10 apex-underline apex-underline-offset-2 hover:apex-text-foreground apex-transition-colors"
                  >
                    {autoSelectedBy[0].name}
                  </button>
                ) : (
                  autoSelectedBy[0].name
                )}
                {autoSelectedBy.length > 1 && (
                  <span className="apex-text-muted-foreground">
                    {" "}
                    +{autoSelectedBy.length - 1} more
                  </span>
                )}
              </span>
            </p>
          )}

          {/* Required-by badge — outlined warning. */}
          {isRequired && relatedItems?.items?.length ? (
            <p
              className="apex-mt-1 apex-text-xs apex-text-warning apex-font-medium"
              aria-label="Required by another selected item"
            >
              Required by{" "}
              {onRelatedClick ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRelatedClick(relatedItems.items[0].id);
                  }}
                  className="apex-underline apex-underline-offset-2 hover:apex-text-foreground apex-transition-colors"
                >
                  {relatedItems.items[0].name}
                </button>
              ) : (
                relatedItems.items[0].name
              )}
              {relatedItems.hasMore && "..."}
            </p>
          ) : null}

          {/* Description (one-line clamp) — full version is in the popover. */}
          {hasDescription && (
            <p className="apex-mt-1 apex-text-xs apex-text-muted-foreground apex-line-clamp-2">
              {item.description}
            </p>
          )}

          {/* Unavailable Reason with clickable links */}
          {isUnavailable && reason && (
            <p className="apex-mt-1 apex-text-xs apex-text-warning apex-flex apex-items-start apex-gap-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="apex-shrink-0 apex-mt-0.5"
                aria-hidden="true"
              >
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
              </svg>
              <span>
                {relatedItems && onRelatedClick ? (
                  <>
                    {relatedItems.type === "requires"
                      ? "Requires: "
                      : relatedItems.type === "enabledBy"
                        ? "Enabled by: "
                        : "Conflicts with: "}
                    {relatedItems.items.map((related, i) => (
                      <span key={related.id}>
                        {i > 0 && ", "}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRelatedClick(related.id);
                          }}
                          className="apex-relative apex-z-10 apex-underline apex-underline-offset-2 hover:apex-text-foreground apex-transition-colors"
                          style={{ cursor: "pointer" }}
                        >
                          {related.name}
                        </button>
                      </span>
                    ))}
                    {relatedItems.hasMore && "..."}
                  </>
                ) : (
                  reason
                )}
              </span>
            </p>
          )}
        </div>

        {/* Info popover */}
        {infoOpen && hasDescription && (
          <div
            id={popoverId}
            ref={popoverRef}
            role="dialog"
            aria-modal="false"
            aria-label={`${item.name || item.code} details`}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "apex-absolute apex-right-2 apex-top-full apex-z-30 apex-mt-1",
              "apex-w-80 apex-max-w-[calc(100%-1rem)]",
              "apex-rounded-md apex-border apex-border-border apex-bg-popover apex-text-popover-foreground",
              "apex-shadow-lg apex-p-3",
            )}
          >
            <div className="apex-flex apex-items-start apex-justify-between apex-gap-2">
              <h5 className="apex-font-semibold apex-text-sm apex-leading-tight">
                {item.name || item.code}
              </h5>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setInfoOpen(false);
                }}
                aria-label="Close info"
                className="apex-shrink-0 apex-rounded apex-p-0.5 apex-text-muted-foreground hover:apex-text-foreground"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>
            <p className="apex-mt-2 apex-text-xs apex-text-muted-foreground apex-whitespace-pre-line">
              {item.description}
            </p>
          </div>
        )}
      </div>
    );
  }
);
